// =============================================================================
// ui/state/useLeakReport.ts — the Coach's Report's own read.
// -----------------------------------------------------------------------------
// A HOOK, not a provider: one surface needs it, and the read is the widest in
// the client per row, so making every tab pay it at boot would be the worst
// possible place to put it.
//
// ── ⚠ THE WINDOW IS A DEPENDENCY, SO CHANGING IT RE-READS ──
//
// Deliberate. The alternative — read the whole book once and slice in memory —
// means every visit to this tab pulls every trade with its fill detail attached,
// to draw a 30-day report. The window is changed rarely and the read is bounded
// by it.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSupabase } from './supabase';
import { loadLeakUniverse, type LeakUniverseTrade } from '../../io/sync/leakUniverse';
import {
  coachPriorWindow, coachWindowStart, readSince, type CoachWindowId,
} from '../../selectors/leaks';
import { reportFailure } from '../../io/diagnostics/errorLog';

export type LeakReportPhase = 'loading' | 'ready' | 'failed';

export interface LeakReportState {
  phase: LeakReportPhase;
  /** The inclusive start date actually fetched — null for the whole book. */
  since: string | null;
  /** ⚠ NULL until a read SUCCEEDED. An empty array means "you traded nothing". */
  trades: readonly LeakUniverseTrade[] | null;
  error: string | null;
  reload: () => void;
}

/**
 * @param todayIso injectable so a test pins the window. The report's whole claim
 *   is "over the last 30 days", and a test that reads the machine's clock
 *   asserts a different window every day it runs.
 * @param reachBack the earliest date an ACTIVE RULE needs, or null when there
 *   are none. It widens the READ only — Spot it still slices to the picker's
 *   window in memory. Without it a rule written before the selected window would
 *   be re-proved against a truncated population, which reads as though the
 *   trader had almost stopped trading. See `readSince`.
 */
export function useLeakReport(
  window: CoachWindowId, todayIso: string, reachBack: string | null = null,
): LeakReportState {
  const { sb } = useSupabase();
  const [phase, setPhase] = useState<LeakReportPhase>('loading');
  const [trades, setTrades] = useState<readonly LeakUniverseTrade[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  // ⚠ THE READ NOW ALSO COVERS THE PRIOR WINDOW, which doubles a 30d fetch for a
  // trader with no active rule (one with a rule was already reaching further
  // back than this). That is the price of the period-over-period strip, and it
  // is paid on the read rather than by a second round trip when entitlement
  // resolves — a fetch that depended on `pro` would re-run the widest read in
  // the client every time the subscription lookup settled.
  const since = useMemo(
    () => readSince(
      coachWindowStart(window, todayIso),
      reachBack,
      coachPriorWindow(window, todayIso)?.start ?? null,
    ),
    [window, todayIso, reachBack],
  );

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);

    void (async () => {
      try {
        const rows = await loadLeakUniverse(sb, since);
        if (cancelled) return;
        setTrades(rows);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        // ⚠ Trades stay NULL rather than emptying. An empty list renders as "no
        // leaks this period", which on a failed read is the most misleading
        // thing this screen could say — it reads as a clean book.
        setTrades(null);
        setError(reportFailure('leak report', e, 'Could not read your trades.'));
        setPhase('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [sb, since, nonce]);

  return useMemo(
    () => ({ phase, since, trades, error, reload }),
    [phase, since, trades, error, reload],
  );
}
